import {
  CloseIcon,
  FilterIcon,
  GcpBox,
  GcpDateRangeRecurring,
  GcpSearchBox,
  GcpTransactionsRecurringTable,
  GcpTypography,
  SearchIcon,
  TransactionRecurringSelectStatus,
} from '@back-office/ui';
import { Button, Divider } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { DateRange } from '@material-ui/pickers';
import { observer } from 'mobx-react';
import React, { FC, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import ExportTransactionsModal from '../ExportTransactionsModal';
import useViewModel from './viewmodel';

export interface Store {
  date: DateRange<Date>;
  handleOnChangeDatePicker: (date: DateRange<Date>) => void;
  handleOnChangePage: (newPage: number) => void;
  handleOnClear: (event: MouseEvent<HTMLOrSVGElement>) => void;
  handleOnClickDatePicker: () => void;
  handleOnCloseDatePicker: () => void;
  handleOnGo: () => void;
  openCalendar: boolean;
  page: number;
  count: number;
  query: string;
  start: number;
  wallet_id: string;
  mobile_no: string;
  status: string;
  batch_name: string;
  reference_no: string;
  transaction_id: string;
  amount: string;
  handleWalletIdSearch: (searchValue: string) => void;
  handleMobileNoSearch: (searchValue: string) => void;
  handleBatchNameSearch: (searchValue: string) => void;
  handleReferenceNoSearch: (searchValue: string) => void;
  handleTransactionIdSearch: (searchValue: string) => void;
  handleAmountSearch: (searchValue: string) => void;
  handleStatusSearch: (value: string) => void;
}

export const useStyles = makeStyles(() => ({
  alert: {
    justifyContent: 'center',
    margin: '16px 0',
  },
  disableBtn: {
    boxShadow: 'none',
    cursor: 'initial',
  },
  container: {
    backgroundColor: '#FAFAFA',
    margin: '8px 0',
    borderRadius: '20px',
  },
}));

const Transaction: FC = observer(() => {
  const { t } = useTranslation();
  const classes = useStyles();
  const {
    state,
    on,
    toggle,
    clearFilter,
    toggleFilterMode,
    filterMode,
    recurringTransaction,
  } = useViewModel();

  return (
    <>
      <Grid container spacing={2}>
        <Grid container item alignItems="center" xs={3}>
          <GcpTypography variant="h3">
            {t('payment_file.transactions')}
          </GcpTypography>
        </Grid>
        <Grid item xs={9}>
          <GcpBox display="flex" gap={2} justifyContent="flex-end">
            <Button
              color="primary"
              size="large"
              startIcon={
                <svg
                  fill="none"
                  height="17"
                  viewBox="0 0 16 17"
                  width="16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.88736 11.1574C7.90072 11.1744 7.9178 11.1883 7.93729 11.1977C7.95678 11.2072 7.97818 11.2122 7.99986 11.2122C8.02154 11.2122 8.04294 11.2072 8.06243 11.1977C8.08192 11.1883 8.099 11.1744 8.11236 11.1574L10.1124 8.62701C10.1856 8.53415 10.1195 8.39665 9.99986 8.39665H8.67665V2.35379C8.67665 2.27522 8.61236 2.21094 8.53379 2.21094H7.46236C7.38379 2.21094 7.3195 2.27522 7.3195 2.35379V8.39487H5.99986C5.88022 8.39487 5.81415 8.53237 5.88736 8.62522L7.88736 11.1574ZM14.5356 10.5324H13.4641C13.3856 10.5324 13.3213 10.5967 13.3213 10.6752V13.4252H2.67843V10.6752C2.67843 10.5967 2.61415 10.5324 2.53557 10.5324H1.46415C1.38557 10.5324 1.32129 10.5967 1.32129 10.6752V14.2109C1.32129 14.527 1.57665 14.7824 1.89272 14.7824H14.107C14.4231 14.7824 14.6784 14.527 14.6784 14.2109V10.6752C14.6784 10.5967 14.6141 10.5324 14.5356 10.5324Z"
                    fill="white"
                  />
                </svg>
              }
              style={{ boxShadow: 'none' }}
              type="button"
              variant="contained"
              onClick={() => toggle(true)}
            >
              {t('payment_file.transactions_list.export')}
            </Button>
            {filterMode ? (
              <GcpBox display="flex" gap={2}>
                <Divider orientation="vertical" />
                <Button
                  className={classes.disableBtn}
                  color="primary"
                  size="large"
                  startIcon={<SearchIcon />}
                  type="button"
                  variant="contained"
                >
                  {t('payment_file.transactions_list.search.key')}
                </Button>
                <Button
                  size="small"
                  style={{ width: '48px' }}
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    clearFilter();
                    toggleFilterMode(false);
                  }}
                >
                  <CloseIcon />
                </Button>
              </GcpBox>
            ) : (
              <Button
                size="large"
                startIcon={<FilterIcon />}
                type="button"
                variant="outlined"
                onClick={() => toggleFilterMode(true)}
              >
                {t('payment_file.transactions_list.filter')}
              </Button>
            )}
          </GcpBox>
        </Grid>
        {filterMode && (
          <Grid
            container
            item
            className={classes.container}
            spacing={3}
            xs={12}
          >
            <Grid item xs={3}>
              <GcpSearchBox
                handleSearch={state.handleWalletIdSearch}
                label={t('payment_file.transactions_list.search.wallet_id')}
                value={state.wallet_id}
              />
            </Grid>
            <Grid item xs={3}>
              <GcpSearchBox
                handleSearch={state.handleMobileNoSearch}
                label={t('payment_file.transactions_list.search.mobile_no')}
                value={state.mobile_no}
              />
            </Grid>
            <Grid item xs={3}>
              <GcpDateRangeRecurring
                date={state.date}
                handleOnChange={state.handleOnChangeDatePicker}
                handleOnClear={state.handleOnClear}
                handleOnClick={state.handleOnClickDatePicker}
                handleOnClose={state.handleOnCloseDatePicker}
                openCalendar={state.openCalendar}
              />
            </Grid>
            <Grid item xs={3}>
              <TransactionRecurringSelectStatus
                value={state.status.toLocaleLowerCase()}
                onChange={state.handleStatusSearch}
              />
            </Grid>

            <Grid item xs={3}>
              <GcpSearchBox
                handleSearch={state.handleBatchNameSearch}
                label={t('payment_file.transactions_list.search.batch_name')}
                value={state.batch_name}
              />
            </Grid>
            <Grid item xs={3}>
              <GcpSearchBox
                handleSearch={state.handleReferenceNoSearch}
                label={t('payment_file.transactions_list.search.reference_no')}
                value={state.reference_no}
              />
            </Grid>
            <Grid item xs={3}>
              <GcpSearchBox
                handleSearch={state.handleTransactionIdSearch}
                label={t(
                  'payment_file.transactions_list.search.transaction_id'
                )}
                value={state.transaction_id}
              />
            </Grid>
            <Grid item xs={3}>
              <GcpSearchBox
                allowOnlyNumeric
                handleSearch={state.handleAmountSearch}
                label={t('payment_file.transactions_list.search.amount')}
                value={state.amount}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
      <GcpBox mt={3}>
        <GcpTransactionsRecurringTable
          count={state.count}
          data={recurringTransaction.list}
          handleOnChangePage={state.handleOnChangePage}
          page={state.page}
          pagination={recurringTransaction.pagination}
        />
      </GcpBox>
      <ExportTransactionsModal handleOnClose={() => toggle(false)} open={on} />
    </>
  );
});

export default Transaction;
